import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to manage PWA install prompt
 * Captures the beforeinstallprompt event and provides install functionality
 */
export const usePWAInstall = () => {
    const [installPrompt, setInstallPrompt] = useState(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        return localStorage.getItem('pwa-install-dismissed') === 'true';
    });

    // Check if already installed
    useEffect(() => {
        // Check for standalone mode (PWA already installed)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            || window.navigator.standalone
            || document.referrer.includes('android-app://');

        setIsInstalled(isStandalone);
    }, []);

    // Capture install prompt
    useEffect(() => {
        const handleBeforeInstall = (e) => {
            // Prevent default browser prompt
            e.preventDefault();
            // Save the event for later use
            setInstallPrompt(e);
            setIsInstallable(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstall);

        // Listen for app installed event
        const handleAppInstalled = () => {
            setIsInstalled(true);
            setIsInstallable(false);
            setInstallPrompt(null);
        };

        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    // Trigger install prompt
    const promptInstall = useCallback(async () => {
        if (!installPrompt) return { outcome: 'unavailable' };

        try {
            // Show the prompt
            installPrompt.prompt();

            // Wait for user choice
            const { outcome } = await installPrompt.userChoice;

            if (outcome === 'accepted') {
                setInstallPrompt(null);
                setIsInstallable(false);
            }

            return { outcome };
        } catch (error) {
            console.error('Error prompting install:', error);
            return { outcome: 'error', error };
        }
    }, [installPrompt]);

    // Dismiss the install banner
    const dismiss = useCallback(() => {
        setDismissed(true);
        localStorage.setItem('pwa-install-dismissed', 'true');
    }, []);

    // Reset dismissed state (for settings)
    const resetDismissed = useCallback(() => {
        setDismissed(false);
        localStorage.removeItem('pwa-install-dismissed');
    }, []);

    return {
        isInstallable: isInstallable && !isInstalled && !dismissed,
        isInstalled,
        dismissed,
        promptInstall,
        dismiss,
        resetDismissed
    };
};

export default usePWAInstall;
